import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/api/api.service';
import { SubFilter } from '@app/core/models/common.models';
import { GlobalFiltersState } from '@app/core/store/states/global-filters/global-filters.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';

import { isObject } from '@app/core/utilities/object.utilities';
import { RemoveOptiTypeFilterForMonitor } from '@app/core/utilities/refiners.utilities';
import { Primitive } from '@app/core/utilities/typescript.utilities';
import { encodeSearchTerms } from '@app/core/utilities/url.utilities';
import { ForcedFilter } from '@app/shared/components';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  pairwise,
  share,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ListItem, SelectorDisplayLabel } from '../models/selector.model';
import { ConvertCase } from './global-filters.utils';

@Injectable()
export class BaseSelectorService {
  hasDestinationSelector = null;
  requiredParams = [];
  limit = 25;
  includedData: any = [];
  cache: any = { items: {} };
  defaultLabel = '';
  defaultSubfiltersType: string;

  readonly forcedFilters$ = new BehaviorSubject<ForcedFilter>({});

  loader$ = new BehaviorSubject(false);
  listLoader$ = new BehaviorSubject(false);

  onSrcListScrolled$ = new BehaviorSubject(0);
  onDestListScrolled$ = new BehaviorSubject(0);

  srcCount$ = new BehaviorSubject<number | string>('');
  destCount$ = new BehaviorSubject<number | string>('');
  srcSub$ = new Subject<any>();
  destSub$ = new Subject<any>();

  srcSelectedItem$;
  destSelectedItem$;

  getSearchValues(filters: any[]) {
    const searchValues = ConvertCase(
      filters.filter(([k]) => k.startsWith('search_')),
      'search_',
    );

    if (Object.keys(searchValues)?.length)
      Object.keys(searchValues).forEach((d) => (searchValues[d] = encodeSearchTerms(searchValues[d])));

    return searchValues;
  }

  dto(d) {
    return d;
  }
  getSelectedItem(id) {
    return of(<ListItem>{});
  }
  getItems(
    d,
    offset = 0,
    forDestination?: boolean,
    forcedFilter?: ForcedFilter,
  ): Observable<{ items: ListItem[]; count: number }> {
    return of(<any>{});
  }

  getForcedFilters(
    forcedFilters: ForcedFilter | undefined,
    forDestination: boolean,
  ): Record<string, Primitive | Primitive[]> {
    const filter = (forDestination ? forcedFilters?.destination : forcedFilters?.source || forcedFilters) as Record<
      string,
      Primitive | Primitive[]
    >;

    return isObject(filter) ? filter : {};
  }

  subfilterOptions() {
    return <SubFilter[]>[];
  }

  readonly filterDefaults = this.store.selectSnapshot(GlobalFiltersState.filtersValues);
  srcItems$ = combineLatest([this.onSrcListScrolled$, this.srcSub$, this.forcedFilters$]).pipe(
    debounceTime(1),
    switchMap(([page, d, forcedFilters]) => {
      if (page !== 0 && this.srcCount$.value === this.cache?.src?.length) return of(this.cache.src);

      return this.getItems(d, page * this.limit, false, forcedFilters).pipe(
        map(({ items, count }) => {
          this.srcCount$.next(count);
          if (page === 0) this.cache.src = items;
          else this.cache.src = [...this.cache.src, ...items];
          return this.cache.src;
        }),
      );
    }),
  );

  destItems$ = combineLatest([this.onDestListScrolled$, this.destSub$, this.forcedFilters$]).pipe(
    debounceTime(1),
    switchMap(([page, d, forcedFilters]) => {
      if (page !== 0 && this.destCount$.value === this.cache?.dest?.length) return of(this.cache.dest);

      return this.getItems(d, page * this.limit, true, forcedFilters).pipe(
        map(({ items, count }) => {
          this.destCount$.next(count);
          if (page === 0) this.cache.dest = items;
          else this.cache.dest = [...this.cache.dest, ...items];
          return this.cache.dest;
        }),
      );
    }),
  );

  selectorParamsChange$ = this.store.select(RouterNavState.queryParams).pipe(
    startWith(null),
    pairwise(),
    filter(([prev, curr]) => !prev || !!this.requiredParams.find((key) => prev[key] !== curr[key])),
    map(([prev, curr]: any) => {
      return this.requiredParams.reduce((acc, d) => {
        acc[d] = curr[d];
        return acc;
      }, {});
    }),
  );

  readonly data$ = this.selectorParamsChange$.pipe(
    switchMap((values) => {
      const def = this.translate.get(this.defaultLabel).pipe(map((payload) => ({ payload, type: 'LABEL' })));
      const obs: Observable<SelectorDisplayLabel>[] = <any>[def, def];

      if (typeof values[this.requiredParams[0]] === 'string') {
        obs[0] = this.srcSelectedItem$.pipe(map((payload) => ({ payload, type: 'SELECTED_ITEM' })));
      }

      if (typeof values[this.requiredParams[2]] === 'string' && !!this.hasDestinationSelector) {
        obs[1] = this.destSelectedItem$.pipe(map((payload) => ({ payload, type: 'SELECTED_ITEM' })));
      }

      if (typeof values[this.requiredParams[1]] === 'string') {
        obs[0] = of({ payload: values[this.requiredParams[1]].split(';'), type: 'FILTER' });
      }

      if (typeof values[this.requiredParams[3]] === 'string') {
        obs[1] = of({
          payload: values[this.requiredParams[3]].split(';'),
          type: 'FILTER',
        });
      }

      return <Observable<[SelectorDisplayLabel, SelectorDisplayLabel, string[]]>>(
        combineLatest([...obs, this.defaultSubfilters$])
      );
    }),
  );

  readonly defaultSubfilters$ = this.store.select(GlobalFiltersState.filtersValues).pipe(
    map((d) => {
      const values = this.store.selectSnapshot(RouterNavState.queryParams);
      const def = RemoveOptiTypeFilterForMonitor(d[this.defaultSubfiltersType]);

      return [this.requiredParams[1], this.requiredParams[3]].map((d) =>
        typeof values[d] === 'string' ? values[d] : def,
      );
    }),
  );

  getSelectedItemStream(param) {
    return this.store.select(RouterNavState.queryParam(param)).pipe(
      filter((d) => !!d),
      switchMap((d) => this.getSelectedItem(d)),
      share(),
    );
  }

  clearCache() {
    this.cache = { items: {} };
  }

  constructor(
    protected translate: TranslateService,
    protected store: Store,
    protected apiService: ApiService,
  ) {}
}
